import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MessageSquarePlus, MessageSquare, AlertCircle } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import type { ConversationListItemDto } from "@/api/messaging";
import { extractErrorMessage } from "@/api/client";
import {
  pausePollingOnSessionExpiry,
  retryUnlessSessionExpired,
} from "@/api/polling";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/design-system/components/ui";
import { NewMessageModal } from "@/components/messaging/NewMessageModal";
import { CountBadge } from "@/components/ui/CountBadge";
import { relativeTime } from "@/components/ux/relativeTime";
import { cn } from "@/design-system/lib/utils";

/**
 * Internal Messaging v1.0 §6 — Inbox. Server-paginated (50/page) list of
 * conversations the caller participates in, ordered by last message desc.
 * Unread rows render bold + gold-tinted background.
 */
export function InboxPage() {
  const [picker, setPicker] = useState(false);
  const [page, setPage] = useState(1);

  const conversations = useQuery({
    queryKey: ["messaging", "conversations", page],
    queryFn: () => messagesApi.listConversations(page, 50),
    // Stop polling once the session expires so an unattended tab doesn't 401
    // every 30s indefinitely.
    refetchInterval: pausePollingOnSessionExpiry(30_000),
    retry: retryUnlessSessionExpired,
  });

  const items = conversations.data?.data ?? [];
  const totalPages = conversations.data
    ? Math.max(1, Math.ceil(conversations.data.totalCount / conversations.data.pageSize))
    : 1;

  // A single peer can now own multiple conversation rows (the "Start a new
  // thread" composer action). When that happens, render a small italic chip
  // on each affected row so the user can tell them apart at a glance.
  const peerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of items) counts.set(c.otherUserId, (counts.get(c.otherUserId) ?? 0) + 1);
    return counts;
  }, [items]);

  return (
    <>
      <PageHeader
        title="Messages"
        description="Internal 1:1 conversations between Phoenix Capital teammates."
        actions={
          <Button onClick={() => setPicker(true)} variant="secondary">
            <MessageSquarePlus size={16} strokeWidth={1.5} aria-hidden /> New message
          </Button>
        }
      />

      <div className="bg-white-pure border border-border-light rounded-lg overflow-hidden">
        {conversations.isLoading && (
          <div className="p-space-4 text-small text-gray-mid">Loading…</div>
        )}
        {!conversations.isLoading && conversations.isError && (
          <div className="p-space-5 flex flex-col items-center text-center gap-space-2" role="alert">
            <AlertCircle size={32} strokeWidth={1.5} className="text-status-error" aria-hidden />
            <div className="font-display font-bold text-status-error">
              Couldn’t load your messages.
            </div>
            <div className="text-small text-gray-mid">
              {extractErrorMessage(conversations.error)}
            </div>
            <Button onClick={() => conversations.refetch()}>Retry</Button>
          </div>
        )}
        {!conversations.isLoading && !conversations.isError && items.length === 0 && (
          <div className="p-space-5 flex flex-col items-center text-center gap-space-2">
            <MessageSquare size={32} strokeWidth={1.5} className="text-gray-mid" aria-hidden />
            <div className="font-display font-bold text-onyx">No messages yet.</div>
            <div className="text-small text-gray-mid">
              Start a conversation with a teammate.
            </div>
            <Button onClick={() => setPicker(true)}>New message</Button>
          </div>
        )}
        {!conversations.isError && (
          <ul className="m-0 p-0 list-none divide-y divide-border-light">
            {items.map((c) => (
              <ConversationRow
                key={c.id}
                c={c}
                showDifferentiator={(peerCounts.get(c.otherUserId) ?? 0) > 1}
              />
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-space-4 py-space-3 border-t border-border-light bg-white-warm">
            <div className="text-label text-gray-mid">
              Page {page} of {totalPages} — {conversations.data?.totalCount ?? 0} conversations
            </div>
            <div className="flex items-center gap-space-2">
              <Button
                variant="tertiary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <NewMessageModal open={picker} onClose={() => setPicker(false)} />
    </>
  );
}

function ConversationRow({
  c,
  showDifferentiator,
}: {
  c: ConversationListItemDto;
  showDifferentiator: boolean;
}) {
  const unread = c.unreadCount > 0;
  const initials = c.otherUserDisplayName
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Prefer the latest attached record's label; fall back to the relative
  // last-message time so adjacent rows for the same peer are still
  // distinguishable. Only rendered when the peer owns more than one row.
  const differentiator = showDifferentiator
    ? c.latestAttachmentLabel ?? (c.lastMessageAt ? relativeTime(c.lastMessageAt) : null)
    : null;

  return (
    <li>
      <Link
        to={`/messages/${c.id}`}
        aria-label={
          unread
            ? `${c.otherUserDisplayName}, ${c.unreadCount} unread message${c.unreadCount === 1 ? "" : "s"}`
            : c.otherUserDisplayName
        }
        className={cn(
          "flex items-center gap-space-3 px-space-4 py-space-3 no-underline text-inherit",
          "hover:bg-white-warm transition-colors",
          // 4px left accent bar — gold for unread, transparent otherwise so
          // the row width / horizontal alignment stays identical between
          // read and unread rows. Spec §3 (unread indicators).
          "border-l-4",
          unread ? "border-l-gold bg-gold/10" : "border-l-transparent",
        )}
      >
        {c.otherUserAvatarUrl ? (
          <img src={c.otherUserAvatarUrl} alt="" className="h-10 w-10 rounded-pill object-cover" />
        ) : (
          <span className="h-10 w-10 rounded-pill bg-gold text-onyx flex items-center justify-center text-small font-display font-bold">
            {initials || "?"}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-space-2">
            <span className={cn(
              "text-small text-onyx truncate",
              unread ? "font-display font-bold" : "font-semibold",
            )}>
              {c.otherUserDisplayName}
              {!c.otherUserIsActive && (
                <span className="text-gray-mid font-normal"> — deactivated</span>
              )}
            </span>
            {c.lastMessageAt && (
              <span className="ml-auto shrink-0 text-label text-gray-mid">
                {relativeTime(c.lastMessageAt)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-space-2">
            <span className={cn(
              "text-small truncate",
              // Spec §3: bold weight on BOTH name and preview when unread.
              unread ? "text-onyx font-semibold" : "text-gray-mid",
            )}>
              {c.lastMessagePreview ?? "No messages yet."}
            </span>
            {differentiator && (
              <span className="shrink-0 text-label text-gray-mid italic">
                · {differentiator}
              </span>
            )}
            <CountBadge
              count={c.unreadCount}
              tone="gold"
              size="md"
              className="ml-auto"
              aria-label={`${c.unreadCount} unread`}
            />
          </div>
        </div>
      </Link>
    </li>
  );
}
